import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';

import _get from 'lodash/get';
import _trim from 'lodash/trim';
import _some from 'lodash/some';
import _defer from 'lodash/defer';

import { fileType } from 'types';
import Notify from 'store/utils/Notify';
import './style.scss';

const NOT_ALLOWED_NAMES = /(intents.xml|entities.json)+$/;
const INVALID_CHAR = /\//g;

/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

export default class File extends Component {
  static propTypes = {
    file: fileType,
    files: PropTypes.arrayOf(fileType),
    hasNameExceptions: PropTypes.bool,
    isSelected: PropTypes.bool,
    selectionsCount: PropTypes.number,
    onAddFile: PropTypes.func,
    onDeleteFile: PropTypes.func,
    onDeleteFiles: PropTypes.func,
    onExportFile: PropTypes.func,
    onExportFiles: PropTypes.func,
    onOpenFile: PropTypes.func,
    onOpenFiles: PropTypes.func,
    onResetState: PropTypes.func,
    onToggleSelection: PropTypes.func,
    onUpdateFile: PropTypes.func,
  };

  state = {
    isEditing: _get(this.props.file, 'isNew', false),
    name: this.props.file.name,
  };

  componentDidMount() {
    if (this.state.isEditing) {
      this.focusInput();
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      (this.props.isSelected &&
        nextProps.selectionsCount !== this.props.selectionsCount) ||
      nextProps.isSelected !== this.props.isSelected ||
      nextProps.file.name !== this.props.file.name ||
      nextProps.file.name !== this.state.name ||
      nextState.name !== this.state.name ||
      nextState.isEditing !== this.state.isEditing
    );
  }

  timer = 0;
  prevent = false;
  delay = 200;

  /**
   * when the input is focused, we want to select the text in the file name input field
   * (except for the file extension), in order to save the user from clicking
   * and selecting it themselves.
   *
   */
  focusInput = () => {
    _defer(() => {
      if (this.input) {
        this.input.focus();
      }
    });
    const fileName = this.state.name;
    const fileExtensionPosition = fileName.indexOf('.');
    const fileExtensionExists = fileExtensionPosition === -1;
    const selectionRangeEnd = fileExtensionExists
      ? fileName.length
      : fileExtensionPosition;

    this.input.setSelectionRange(0, selectionRangeEnd);
  };

  resetState = () => {
    this.setState({
      isEditing: false,
      name: this.props.file.name,
    });
  };

  fileNameExists = (name, id) =>
    _some(this.props.files, file => file.name === name && file.id !== id);

  showErrorNotification = description => {
    Notify.error({
      title: 'An error occurred',
      description,
    });
    this.setState({
      name: this.props.file.name,
    });
    this.focusInput();
  };

  fileExtensionIsValid = name =>
    !this.props.hasNameExceptions || !name.endsWith('.nlu');

  fileNameIsValid = name =>
    !name.match(INVALID_CHAR) &&
    (!this.props.hasNameExceptions || !name.match(NOT_ALLOWED_NAMES));

  handleSave = () => {
    const name = _trim(this.state.name);
    const { file, onUpdateFile, onAddFile } = this.props;
    const isNew = _get(file, 'isNew', false);

    if (!name) {
      // Add new file
      if (isNew) {
        this.showErrorNotification('Please enter a name');
        return;
      }

      // Update existing file
      this.resetState();

      return;
    }

    if (!this.fileNameIsValid(name)) {
      this.showErrorNotification('File name is not allowed');
      return;
    }

    if (!this.fileExtensionIsValid(name)) {
      this.showErrorNotification('File extension is not valid');
      return;
    }

    if (this.fileNameExists(name, file.id)) {
      this.showErrorNotification(`An item with name "${name}" already exists.`);
      return;
    }
    // is new file
    if (isNew) {
      onAddFile(name);
    } else if (name !== file.name) {
      onUpdateFile(file, name);
    }

    this.setState({ isEditing: false });
  };

  handleExport = () => {
    const {
      isSelected,
      selectionsCount,
      file,
      onExportFile,
      onExportFiles,
    } = this.props;

    if (!isSelected || (isSelected && selectionsCount === 1)) {
      onExportFile(file);
      return;
    }
    onExportFiles();
  };

  handleDelete = () => {
    if (!this.props.isSelected) {
      this.props.onDeleteFile(this.props.file);
      return;
    }

    this.props.onDeleteFiles();
  };

  handleEdit = () => {
    this.setState(
      {
        isEditing: true,
      },
      this.focusInput,
    );
  };

  handleOpen = () => {
    if (!this.props.isSelected) {
      this.props.onOpenFile(this.props.file);
      return;
    }
    this.props.onOpenFiles();
  };

  handleToggleSelection = e => {
    // remove the synthetic event from the pool and allow references
    // to the event to be retained (https://reactjs.org/docs/events.html)
    e.persist();
    this.timer = setTimeout(() => {
      if (!this.prevent) {
        if (this.props.isSelected && this.props.selectionsCount === 1) {
          this.handleEdit();
        }
        this.props.onToggleSelection(e, this.props.file);
      }
      this.prevent = false;
    }, this.delay);
  };

  handleChange = ({ target: { value } }) => {
    this.setState({
      name: value,
    });
  };

  handleKeyDown = e => {
    // esc
    if (e.which === 27) {
      this.resetState();
    }

    // enter
    if (e.which === 13) {
      this.handleSave();
    }
  };

  handleDoubleClick = () => {
    clearTimeout(this.timer);
    this.prevent = true;
    this.props.onOpenFile(this.props.file);
    this.props.onResetState();
  };

  renderMenuItemText = () => {
    const { selectionsCount, isSelected } = this.props;
    if (!isSelected) {
      return null;
    }

    return selectionsCount > 1 ? ` ${selectionsCount} items` : '';
  };

  renderMenu() {
    const menuItemText = this.renderMenuItemText();
    return (
      <ContextMenu
        data-test="file-context-menu"
        id={this.props.file.id}
        styleName="menu"
      >
        <div data-test="file-context-soft-actions" styleName="menu-group">
          <MenuItem
            attributes={{ 'data-test': 'open' }}
            onClick={this.handleOpen}
          >
            Open {menuItemText}
          </MenuItem>
          {this.props.selectionsCount <= 1 && (
            <MenuItem
              attributes={{ 'data-test': 'edit' }}
              onClick={this.handleEdit}
            >
              Rename {menuItemText}
            </MenuItem>
          )}
          <MenuItem
            attributes={{ 'data-test': 'export' }}
            onClick={this.handleExport}
          >
            Export {menuItemText}
          </MenuItem>
        </div>
        <div data-test="file-context-hard-actions" styleName="menu-group">
          <MenuItem
            attributes={{ 'data-test': 'delete' }}
            onClick={this.handleDelete}
          >
            Delete {menuItemText}
          </MenuItem>
        </div>
      </ContextMenu>
    );
  }

  render() {
    const { isEditing, name } = this.state;
    return (
      <li
        className="icon-file"
        styleName={cn('file', {
          selected: this.props.isSelected,
        })}
      >
        {!isEditing ? (
          <ContextMenuTrigger id={this.props.file.id}>
            <div
              data-test="name"
              styleName="name"
              onClick={this.handleToggleSelection}
              onDoubleClick={this.handleDoubleClick}
            >
              {name}
            </div>
          </ContextMenuTrigger>
        ) : (
          <input
            ref={el => {
              this.input = el;
            }}
            data-test="file-name-input"
            name="name"
            placeholder="File name"
            size={name.length}
            value={name}
            onBlur={this.handleSave}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
          />
        )}
        {this.renderMenu()}
      </li>
    );
  }
}
